// API Types for Backend Integration

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// Magic link purpose types
export type SignInCodePurpose = 'login' | 'verify_email' | 'sso_link';

export interface SignInCodeRequest {
  email: string;
  purpose: SignInCodePurpose;
  locale: string;
  client: string;
  client_ip: string;
  user_agent: string;
  activation_code?: string;
}

export interface SignInCodeResponse {
  message: string;
  /** How long clients should disable their resend button, in seconds. */
  resend_in_seconds: number;
}

export interface VerifyCodeRequest {
  email: string;
  code: string;
  client: string;
  client_ip: string;
  user_agent: string;
  invite_code?: string;
}

export interface VerifyCodeResponse {
  user_id: string;
  is_new_user: boolean;
  access_token: string;
  refresh_token: string;
}

export interface RefreshTokenRequest {
  refresh_token: string;
}

export interface RefreshTokenResponse {
  access_token: string;
}

export interface AgentLinkCodeCreateResponse {
  code: string;
  deep_link: string;
  expires_at: string;
}

export interface AgentLinkStatusResponse {
  linked: boolean;
  last_linked_at?: string;
  linked_at?: string;
  updated_at?: string;
  telegram_username?: string;
}

export class ApiError extends Error {
  public readonly code: string;
  public readonly details?: Record<string, unknown>;

  constructor(code: string, message: string, details?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.details = details;
  }
}

export interface AuthState {
  isAuthenticated: boolean;
  user: {
    id: string;
    email: string;
    name?: string;
  } | null;
  token: string | null;
}

// Error types for different scenarios
export type AuthError = 
  | 'INVALID_EMAIL'
  | 'EMAIL_NOT_FOUND'
  | 'TOKEN_EXPIRED'
  | 'TOKEN_INVALID'
  | 'TOKEN_USED'
  | 'REFRESH_TOKEN_EXPIRED'
  | 'REFRESH_TOKEN_INVALID'
  | 'RATE_LIMITED'
  | 'NETWORK_ERROR'
  | 'SERVER_ERROR'
  | 'UNKNOWN_ERROR';

export interface AuthErrorResponse {
  error: AuthError;
  message: string;
  retryAfter?: number; // seconds
}

// Pagination types matching backend PaginatedResponse
export interface Pagination {
  next_cursor?: string;
  has_more: boolean;
  limit: number;
  total?: number;
  total_count?: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: Pagination;
}

// WebAuthn Types
export interface WebAuthnCheckByEmailRequest {
  email: string;
}

export interface WebAuthnCheckByEmailResponse {
  has_devices: boolean;
  user_id?: string;
}

export interface WebAuthnRegisterBeginRequest {
  user_id: string;
}

export interface WebAuthnRegisterBeginResponse {
  _session_id?: string;
  publicKey: PublicKeyCredentialCreationOptions;
}

export interface WebAuthnRegisterCompleteRequest {
  user_id: string;
  credential: PublicKeyCredentialJSON;
  device_name: string;
  device_type: 'mobile' | 'desktop' | 'tablet';
  _session_id?: string;
}

export interface WebAuthnRegisterCompleteResponse {
  credential_id: string;
  message: string;
}

export interface WebAuthnAuthenticateBeginRequest {
  user_id: string;
}

export interface WebAuthnAuthenticateBeginResponse {
  publicKey: PublicKeyCredentialRequestOptions & { _session_id?: string };
}

export interface WebAuthnAuthenticateCompleteRequest {
  user_id: string;
  credential: PublicKeyCredentialJSON;
  client: string;
  client_ip: string;
  user_agent: string;
  _session_id?: string;
}

export interface WebAuthnAuthenticateCompleteResponse {
  user_id: string;
  access_token: string;
  refresh_token: string;
}

export interface WebAuthnDevice {
  credential_id: string;
  device_name: string;
  device_type: 'mobile' | 'desktop' | 'tablet';
  registered_at: string;
  last_used_at: string;
  is_active: boolean;
}

export interface WebAuthnListDevicesResponse {
  devices: WebAuthnDevice[];
}

export interface WebAuthnRevokeDeviceResponse {
  message: string;
}

// WebAuthn Credential JSON format (from browser API)
export interface PublicKeyCredentialJSON {
  id: string;
  rawId: string;
  response: AuthenticatorAttestationResponseJSON | AuthenticatorAssertionResponseJSON;
  type: 'public-key';
}

export interface AuthenticatorAttestationResponseJSON {
  clientDataJSON: string;
  attestationObject: string;
  transports?: AuthenticatorTransport[];
}

export interface AuthenticatorAssertionResponseJSON {
  clientDataJSON: string;
  authenticatorData: string;
  signature: string;
  userHandle?: string;
}

// Browser WebAuthn API types (extended)
export interface PublicKeyCredentialCreationOptions {
  _session_id?: string;
  challenge: Uint8Array;
  rp: {
    name: string;
    id: string;
  };
  user: {
    id: Uint8Array;
    name: string;
    displayName: string;
  };
  pubKeyCredParams: Array<{
    type: 'public-key';
    alg: number;
  }>;
  timeout?: number;
  attestation?: 'none' | 'indirect' | 'direct' | 'enterprise';
  authenticatorSelection?: {
    authenticatorAttachment?: 'platform' | 'cross-platform';
    requireResidentKey?: boolean;
    userVerification?: 'required' | 'preferred' | 'discouraged';
  };
  excludeCredentials?: Array<{
    id: Uint8Array;
    type: 'public-key';
    transports?: AuthenticatorTransport[];
  }>;
}

export interface PublicKeyCredentialRequestOptions {
  challenge: Uint8Array;
  timeout?: number;
  rpId?: string;
  allowCredentials?: Array<{
    id: Uint8Array;
    type: 'public-key';
    transports?: AuthenticatorTransport[];
  }>;
  userVerification?: 'required' | 'preferred' | 'discouraged';
}
